import { useLazyGetInspectorsQuery } from 'app/features/account-management/api';
import { useLazyGetLocationsQuery } from 'app/features/shared/api';
import { useLazyGetCategoriesQuery } from 'app/features/static/api';
import React from 'react';
import { AuthContext } from '../Authentication';
import { AQLLevel, inspectionType, inspectionLevel } from './options';

export enum DataType {
  COUNTRIES = 'countries',
  CATEGORIES = 'categories',
  INSPECTORS = 'inspectors',
  INSPECTION_LEVEL = 'inspectionLevel',
  INSPECTION_TYPE = 'inspectionType',
  AQL = 'aql'
}
const OPTIONS: Record<
  DataType,
  Array<{ value: string | number; label: string }>
> = {
  [DataType.COUNTRIES]: [],
  [DataType.CATEGORIES]: [],
  [DataType.AQL]: AQLLevel,
  [DataType.INSPECTION_LEVEL]: inspectionLevel,
  [DataType.INSPECTION_TYPE]: inspectionType,
  [DataType.INSPECTORS]: []
};
type StaticType = {
  countries: Array<any>;
  categories: Array<any>;
  inspectors: Array<{ value: string; label: string }>;
  inspectionLevel?: Array<{ value: string; label: string }>;
  inspectionType?: Array<{ value: string; label: string }>;
  aql?: Array<{ value: string | number; label: string }>;
  getOptions: (dataType: DataType) => Array<{ value: string; label: string }>;
};

export const StaticContext = React.createContext<StaticType>({
  countries: [],
  categories: [],
  inspectors: [],
  inspectionLevel: inspectionLevel,
  inspectionType: inspectionType,
  aql: AQLLevel,
  getOptions: () => []
});

const StaticProvider = ({ children }: any) => {
  const { token } = React.useContext(AuthContext);
  const [fetchCountries, resultCountries] = useLazyGetLocationsQuery();
  const [fetchCategories, resultCategories] = useLazyGetCategoriesQuery();
  const [fetchInspectors, resultInspectors] = useLazyGetInspectorsQuery();
  const getOptions = (dataType: DataType) => {
    const excludeParser = [
      DataType.AQL,
      DataType.INSPECTION_LEVEL,
      DataType.INSPECTION_TYPE
    ];
    let options = [];
    if (dataType === DataType.CATEGORIES) {
      options = resultCategories.data?.data ?? [];
    }
    if (dataType === DataType.INSPECTORS) {
      options = (resultInspectors.data?.data ?? []).map((inspector) => {
        return {
          id: inspector.id,
          name: `${inspector.firstName} ${inspector.lastName}`
        };
      });
    }
    if (dataType === DataType.COUNTRIES) {
      options = (resultCountries?.data?.data ?? []).map((country) => {
        return {
          id: country.id,
          name: country?.country
        };
      });
    }
    if (excludeParser.includes(dataType)) {
      return (OPTIONS as any)[dataType];
    }
    return options.map(({ id, name, name_en }) => ({
      value: id,
      label: name || name_en
    }));
  };

  React.useEffect(() => {
    fetchCategories({
      current: 1,
      pageSize: 99999 // hack to get all categories.
    });
    fetchInspectors({
      current: 1,
      pageSize: 99999 // hack to get all inspectors.
    });
    fetchCountries({
      current: 1,
      pageSize: 99999 // hack to get all inspectors.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <StaticContext.Provider
      value={{
        countries: resultCountries.data?.data as any[],
        inspectors: resultInspectors.data?.data as any[],
        categories: resultCategories.data?.data as any[],
        inspectionLevel: inspectionLevel,
        inspectionType: inspectionType,
        aql: AQLLevel,
        getOptions
      }}
    >
      {children}
    </StaticContext.Provider>
  );
};

export const useMasterData = () => React.useContext(StaticContext);
export default StaticProvider;
